import type {
  BaseballStats,
  BeerDuty,
  PlayerListItem,
} from "../shared/types/contracts";

export const STAT_COLUMNS = [
  "AB",
  "H",
  "1B",
  "2B",
  "3B",
  "HR",
  "BB",
  "K",
  "RBI",
  "R",
  "SF",
] as const satisfies ReadonlyArray<keyof BaseballStats>;

export const SEASON_TABLE_COLUMNS = [
  "AB",
  "H",
  "1B",
  "2B",
  "3B",
  "HR",
  "BB",
  "K",
  "RBI",
  "R",
] as const satisfies ReadonlyArray<keyof BaseballStats>;

export const RATE_TABLE_COLUMNS = ["AVG", "SLG", "OPS", "OBP", "ISO"] as const;

export interface RateStats {
  avg: number;
  obp: number;
  slg: number;
  ops: number;
  iso: number;
}

export interface CurrentStatRow extends RateStats {
  p: PlayerListItem;
  s: BaseballStats;
  pa: number;
}

export interface ArchivedGameStats {
  id?: string;
  seasonId?: string | null;
  status?: string;
  date: string;
  scheduledDate?: string | null;
  timeSlot?: "early" | "late" | null;
  field?: string | null;
  isHome?: boolean;
  beerDuty?: BeerDuty | null;
  scheduleId?: string | null;
  playoffSeriesId?: string | null;
  seriesGameNumber?: number | null;
  lineup?: string[];
  lineScore?: { us: number[]; them: number[] };
  opponent: string;
  ourScore: number;
  oppScore: number;
  stats: Record<string, BaseballStats>;
  playerStats?: Array<{
    playerId: string | null;
    name: string;
    stats: BaseballStats;
  }>;
  forfeitWinner?: "us" | "them" | null;
}

export interface SeasonTotals extends BaseballStats {
  GP: number;
}

export interface SeasonStatRow extends RateStats {
  id?: string;
  name: string;
  s: SeasonTotals;
}

export interface SeasonSummary {
  wins: number;
  losses: number;
  ties: number;
  rows: SeasonStatRow[];
}

export function emptyBaseballStats(): BaseballStats {
  return {
    AB: 0,
    H: 0,
    "1B": 0,
    "2B": 0,
    "3B": 0,
    HR: 0,
    BB: 0,
    K: 0,
    RBI: 0,
    R: 0,
    SF: 0,
  };
}

export function formatRate(value: number): string {
  return isNaN(value) ? ".000" : value.toFixed(3).replace(/^0/, "");
}

export function calculateRates(stats: BaseballStats): RateStats {
  const totalBases =
    stats["1B"] + 2 * stats["2B"] + 3 * stats["3B"] + 4 * stats.HR;
  const avg = stats.H / stats.AB;
  const obp = (stats.H + stats.BB) / (stats.AB + stats.BB + stats.SF);
  const slg = totalBases / stats.AB;
  return {
    avg,
    obp,
    slg,
    ops: obp + (stats.AB ? slg : 0),
    iso: stats.AB ? slg - avg : 0,
  };
}

export function calculateExportRates(
  stats: Pick<
    BaseballStats,
    "AB" | "H" | "1B" | "2B" | "3B" | "HR" | "BB" | "SF"
  >,
): { AVG: number; SLG: number; OPS: number; OBP: number; ISO: number } {
  const totalBases =
    stats["1B"] + 2 * stats["2B"] + 3 * stats["3B"] + 4 * stats.HR;
  const AVG = stats.AB ? Number((stats.H / stats.AB).toFixed(3)) : 0;
  const OBP =
    stats.AB + stats.BB + stats.SF
      ? Number(
          ((stats.H + stats.BB) / (stats.AB + stats.BB + stats.SF)).toFixed(3),
        )
      : 0;
  const SLG = stats.AB ? Number((totalBases / stats.AB).toFixed(3)) : 0;
  return {
    AVG,
    SLG,
    OPS: Number((OBP + SLG).toFixed(3)),
    OBP,
    ISO: Number((SLG - AVG).toFixed(3)),
  };
}

export function sortByOps<T extends { ops: number }>(rows: T[]): T[] {
  return [...rows].sort((left, right) => {
    const leftHasOps = Number.isFinite(left.ops);
    const rightHasOps = Number.isFinite(right.ops);
    if (leftHasOps !== rightHasOps) return leftHasOps ? -1 : 1;
    if (!leftHasOps) return 0;
    return right.ops - left.ops;
  });
}

export function buildCurrentStatRows(
  players: PlayerListItem[],
  statsByPlayerId: Record<string, BaseballStats>,
): CurrentStatRow[] {
  return sortByOps(
    players.map((player) => {
      const stats = statsByPlayerId[player.id] || emptyBaseballStats();
      return {
        p: player,
        s: stats,
        pa: stats.AB + stats.BB + stats.SF,
        ...calculateRates(stats),
      };
    }),
  );
}

export function buildSeasonSummary(
  gameArchive: ArchivedGameStats[],
  players: PlayerListItem[] = [],
): SeasonSummary {
  const totals: Record<string, SeasonTotals> = {};
  const names: Record<string, string> = {};
  const currentNames = new Map(
    players.map((player) => [player.id, player.name]),
  );
  let wins = 0;
  let losses = 0;
  let ties = 0;

  for (const game of gameArchive) {
    if (game.forfeitWinner === "us") wins += 1;
    else if (game.forfeitWinner === "them") losses += 1;
    else if (game.ourScore > game.oppScore) wins += 1;
    else if (game.ourScore < game.oppScore) losses += 1;
    else ties += 1;

    const isForfeit = Boolean(game.forfeitWinner);
    const statLines =
      game.playerStats ??
      Object.entries(game.stats).map(([name, stats]) => ({
        playerId: null,
        name,
        stats,
      }));
    for (const line of statLines) {
      const { playerId, name, stats: gameStats } = line;
      if (
        isForfeit &&
        STAT_COLUMNS.every((column) => !(gameStats[column] ?? 0))
      ) {
        continue;
      }
      const key = playerId ? `player:${playerId}` : `legacy:${name}`;
      names[key] = playerId ? (currentNames.get(playerId) ?? name) : name;
      const playerTotals = totals[key] ?? {
        GP: 0,
        ...emptyBaseballStats(),
      };
      if (!isForfeit) playerTotals.GP += 1;
      for (const column of STAT_COLUMNS) {
        playerTotals[column] += gameStats[column] ?? 0;
      }
      totals[key] = playerTotals;
    }
  }

  const rows = Object.entries(totals)
    .map(([id, stats]) => {
      const rates = calculateRates(stats);
      return {
        id,
        name: names[id] ?? id,
        s: stats,
        avg: stats.AB ? rates.avg : 0,
        obp: stats.AB + stats.BB + stats.SF ? rates.obp : 0,
        slg: stats.AB ? rates.slg : 0,
        ops: stats.AB + stats.BB + stats.SF ? rates.ops : 0,
        iso: stats.AB ? rates.iso : 0,
      };
    })
    .sort((a, b) => b.avg - a.avg);

  return { wins, losses, ties, rows };
}
